import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import {
  Plus,
  Search,
  LayoutGrid,
  List,
  Trash2,
  Edit3,
  X,
  Check,
  ExternalLink,
  CheckSquare,
  Flag,
  CheckCircle2,
  Circle,
  Clock,
  Calendar,
  ArrowRight,
  AlertCircle,
  Sparkles,
  Send,
  RotateCcw
} from 'lucide-react';
import { api } from '@/lib/api.js';
import { useAuth } from '@/context/AuthContext.js';
import { SortButton } from '@/components/ui/SortButton.js';
import { Pagination } from '@/components/ui/Pagination.js';
import { PullToRefresh } from '@/components/ui/PullToRefresh.js';
import { DeleteConfirmModal } from '@/components/ui/DeleteConfirmModal.js';
import { SkeletonTable, SkeletonCard, Skeleton } from '@/components/ui/skeleton.js';
import { Milestone, MilestoneStatus, Project, PlatformType, Task } from '@/types/index.js';
import { PlatformBadge } from '@/components/ui/PlatformBadge.js';
import { DeliveryCountdownBadge } from '@/components/ui/DeliveryCountdownBadge.js';
import { formatExternalUrl } from '@/lib/utils.js';
import { OrderImportModal } from '@/components/platform/OrderImportModal.js';
import { ChannelDeliveryModal } from '@/components/platform/ChannelDeliveryModal.js';
import { PlatformProfitabilityCard } from '@/components/platform/PlatformProfitabilityCard.js';
import { TOSComplianceGuard } from '@/components/platform/TOSComplianceGuard.js';
import { calculatePlatformCut, calculateEffectiveHourlyYield } from '@/lib/platformFees.js';

export function ProjectsPage() {
  const { idOrSlug } = useParams<{ idOrSlug?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || 'team_member';
  const isOwner = role === 'owner';
  const canManageProjects = isOwner || role === 'manager';
  const isViewer = role === 'viewer';

  const canEditTask = (t: Task) => {
    if (isViewer) return false;
    if (canManageProjects) return true;
    if (t.assignee_id && user?.id && t.assignee_id === user.id) return true;
    if (t.assignee_name && user?.name && t.assignee_name.toLowerCase().includes(user.name.toLowerCase())) return true;
    return false;
  };

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [platformTab, setPlatformTab] = useState<'all' | 'fiverr' | 'upwork' | 'direct' | 'internal'>('all');
  const [platformFilter, setPlatformFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortField, setSortField] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [deliveryModalProject, setDeliveryModalProject] = useState<Project | null>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    category: 'WordPress',
    priority: 'medium',
    client_name: '',
    client_username: '',
    platform: 'direct' as PlatformType,
    platform_url: '',
    platform_order_id: '',
    deadline: '',
    status: 'active'
  });

  // Detail & Edit State
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [deleteProjectTarget, setDeleteProjectTarget] = useState<Project | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Milestones State inside Detail View
  const [projectMilestones, setProjectMilestones] = useState<Milestone[]>([]);
  const [loadingMilestones, setLoadingMilestones] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'milestones'>('overview');
  const [showAddMilestoneModal, setShowAddMilestoneModal] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<Milestone | null>(null);
  const [deleteMilestoneTarget, setDeleteMilestoneTarget] = useState<Milestone | null>(null);
  const [milestoneForm, setMilestoneForm] = useState({
    title: '',
    description: '',
    due_date: '',
    status: 'pending' as MilestoneStatus,
    order: 0,
  });

  // Close modals on Escape key
  useEffect(() => {
    if (!showCreateModal && !selectedProject && !deleteProjectTarget && !deleteMilestoneTarget && !showAddMilestoneModal && !editingMilestone) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (deleteMilestoneTarget) {
          setDeleteMilestoneTarget(null);
        } else if (deleteProjectTarget) {
          setDeleteProjectTarget(null);
        } else if (editingMilestone) {
          setEditingMilestone(null);
        } else if (showAddMilestoneModal) {
          setShowAddMilestoneModal(false);
        } else if (showCreateModal) {
          setShowCreateModal(false);
        } else if (selectedProject) {
          handleCloseDetail();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showCreateModal, selectedProject, deleteProjectTarget, deleteMilestoneTarget, showAddMilestoneModal, editingMilestone]);

  // Project Tasks inside Detail View
  const [projectTasks, setProjectTasks] = useState<any[]>([]);
  const [projectTaskMilestoneFilter, setProjectTaskMilestoneFilter] = useState('all');
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [newTaskForm, setNewTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    milestone_id: '',
  });

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const handleCloseDetail = () => {
    setSelectedProject(null);
    setIsEditing(false);
    setShowAddTaskModal(false);
    setShowAddMilestoneModal(false);
    setEditingMilestone(null);
    setDeleteMilestoneTarget(null);
    setActiveTab('overview');
    if (idOrSlug) {
      navigate('/projects', { replace: true });
    }
  };

  const fetchProjectMilestones = async (projectId: string) => {
    try {
      setLoadingMilestones(true);
      const res = await api.get<{ data: Milestone[] }>(`/api/v1/milestones?project_id=${encodeURIComponent(projectId)}`);
      const list = res?.data || (Array.isArray(res) ? res : []);
      setProjectMilestones(list);
    } catch (e) {
      console.error('Error fetching project milestones:', e);
    } finally {
      setLoadingMilestones(false);
    }
  };

  const fetchProjectTasks = async (projectId: string) => {
    try {
      const res = await api.get<{ data: any[] }>('/api/v1/tasks');
      const allTasks = res?.data || (Array.isArray(res) ? res : []);
      setProjectTasks(allTasks.filter((t: any) => t.project_id === projectId));
    } catch (e) {
      console.error('Error fetching project tasks:', e);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await api.get<{ data: Project[] }>('/api/v1/projects');
      const list = res?.data || (Array.isArray(res) ? res : []);
      setProjects(list);
      if (canManageProjects) {
        api.get<{ data: any[] }>('/api/v1/invoices').then((invRes) => {
          const invList = invRes?.data || (Array.isArray(invRes) ? invRes : []);
          setInvoices(invList);
        }).catch(() => {});
      }
    } catch (e) {
      console.error('Error fetching projects:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, categoryFilter, priorityFilter, platformTab, platformFilter]);

  // Deep linking via /projects/:idOrSlug
  useEffect(() => {
    if (idOrSlug && projects.length > 0) {
      const found = projects.find((p) => p.id === idOrSlug || p.slug === idOrSlug);
      if (found) {
        handleOpenDetail(found);
      }
    }
  }, [idOrSlug, projects]);

  const filteredProjects = projects.filter((p) => {
    const pPlatform = (p.platform || 'direct').toLowerCase();
    const matchesTab = platformTab === 'all' || pPlatform === platformTab;
    const matchesPlatformFilter = !platformFilter || pPlatform === platformFilter.toLowerCase();
    const matchesStatus = statusFilter ? p.status === statusFilter : true;
    const matchesCategory = categoryFilter ? p.category === categoryFilter : true;
    const matchesPriority = priorityFilter ? p.priority === priorityFilter : true;
    const matchesSearch = search
      ? p.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.client_name?.toLowerCase().includes(search.toLowerCase()) ||
        p.client_username?.toLowerCase().includes(search.toLowerCase()) ||
        p.platform_order_id?.toLowerCase().includes(search.toLowerCase()) ||
        p.category?.toLowerCase().includes(search.toLowerCase()) ||
        p.slug?.toLowerCase().includes(search.toLowerCase())
      : true;
    return matchesTab && matchesPlatformFilter && matchesStatus && matchesCategory && matchesPriority && matchesSearch;
  });

  const channelCounts = {
    all: projects.length,
    fiverr: projects.filter((p) => (p.platform || 'direct').toLowerCase() === 'fiverr').length,
    upwork: projects.filter((p) => (p.platform || 'direct').toLowerCase() === 'upwork').length,
    direct: projects.filter((p) => (p.platform || 'direct').toLowerCase() === 'direct').length,
    internal: projects.filter((p) => (p.platform || 'direct').toLowerCase() === 'internal').length,
  };

  const hasActiveFilters =
    platformTab !== 'all' ||
    Boolean(platformFilter) ||
    Boolean(statusFilter) ||
    Boolean(categoryFilter) ||
    Boolean(priorityFilter) ||
    Boolean(search);

  const handleResetFilters = () => {
    setPlatformTab('all');
    setPlatformFilter('');
    setStatusFilter('');
    setCategoryFilter('');
    setPriorityFilter('');
    setSearch('');
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedProjects = [...filteredProjects].sort((a: any, b: any) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';
    if (typeof aVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  });

  const pageSize = 25;
  const totalPages = Math.max(1, Math.ceil(sortedProjects.length / pageSize));
  const validCurrentPage = Math.min(currentPage, totalPages);
  const paginatedProjects = sortedProjects.slice((validCurrentPage - 1) * pageSize, validCurrentPage * pageSize);

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProject.name.trim()) return;
    try {
      const res = await api.post('/api/v1/projects', {
        name: newProject.name.trim(),
        description: newProject.description.trim(),
        category: newProject.category,
        priority: newProject.priority,
        client_name: newProject.client_name.trim() || 'Direct Client',
        client_username: newProject.client_username.trim() || null,
        platform: newProject.platform || 'direct',
        platform_url: formatExternalUrl(newProject.platform_url) || null,
        platform_order_id: newProject.platform_order_id.trim() || null,
        status: newProject.status || 'active',
        deadline: newProject.deadline ? newProject.deadline : null,
      });
      const created = res?.data || res;
      if (created && created.id) {
        setProjects((prev) => [created, ...prev.filter((p) => p.id !== created.id)]);
      }
      setShowCreateModal(false);
      setNewProject({
        name: '',
        description: '',
        category: 'WordPress',
        priority: 'medium',
        client_name: '',
        client_username: '',
        platform: 'direct',
        platform_url: '',
        platform_order_id: '',
        deadline: '',
        status: 'active'
      });
      showToast(`Project "${created?.name || 'New Project'}" created successfully!`);
      await fetchProjects();
    } catch (e: any) {
      alert(e.message || 'Failed to create project');
    }
  };

  const handleOpenDetail = (proj: Project) => {
    navigate(`/projects/${proj.id}`);
  };

  const handleCreateMilestone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject || !milestoneForm.title.trim()) return;
    try {
      await api.post('/api/v1/milestones', {
        project_id: selectedProject.id,
        title: milestoneForm.title.trim(),
        description: milestoneForm.description.trim() || null,
        due_date: milestoneForm.due_date || null,
        status: milestoneForm.status,
        order: Number(milestoneForm.order) || (projectMilestones.length + 1)
      });
      showToast('Milestone created successfully');
      setShowAddMilestoneModal(false);
      setMilestoneForm({ title: '', description: '', due_date: '', status: 'pending', order: 0 });
      fetchProjectMilestones(selectedProject.id);
    } catch (err: any) {
      console.error('Failed to create milestone:', err);
      showToast(err.message || 'Failed to create milestone');
    }
  };

  const handleSaveEditMilestone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject || !editingMilestone || !milestoneForm.title.trim()) return;
    try {
      await api.put(`/api/v1/milestones/${editingMilestone.id}`, {
        title: milestoneForm.title.trim(),
        description: milestoneForm.description.trim() || null,
        due_date: milestoneForm.due_date || null,
        status: milestoneForm.status,
        order: Number(milestoneForm.order) || 0
      });
      showToast('Milestone updated successfully');
      setEditingMilestone(null);
      fetchProjectMilestones(selectedProject.id);
    } catch (err: any) {
      console.error('Failed to update milestone:', err);
      showToast(err.message || 'Failed to update milestone');
    }
  };

  const handleMoveTaskMilestone = async (taskId: string, targetMilestoneId: string | null) => {
    try {
      await api.patch(`/api/v1/tasks/${taskId}`, {
        milestone_id: targetMilestoneId,
      });
      setProjectTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, milestone_id: targetMilestoneId } : t))
      );
      if (targetMilestoneId) {
        const targetM = projectMilestones.find((m) => m.id === targetMilestoneId);
        showToast(`Task moved to ${targetM?.title || 'milestone'}`);
      } else {
        showToast('Task unlinked from milestone');
      }
    } catch (err: any) {
      showToast(err?.message || 'Failed to update task milestone');
    }
  };

  const handleToggleMilestoneStatus = async (m: Milestone) => {
    if (!canManageProjects) return;
    const nextStatus: MilestoneStatus =
      m.status === 'pending'
        ? 'in_progress'
        : m.status === 'in_progress'
        ? 'completed'
        : 'pending';

    // Optimistic UI update
    setProjectMilestones((prev) =>
      prev.map((item) => (item.id === m.id ? { ...item, status: nextStatus } : item))
    );

    try {
      await api.put(`/api/v1/milestones/${m.id}`, { status: nextStatus });
      showToast(`Milestone marked as ${nextStatus.replace('_', ' ')}`);
      if (selectedProject) {
        fetchProjectMilestones(selectedProject.id);
      }
    } catch (err: any) {
      console.error('Failed to toggle milestone status:', err);
      showToast(err.message || 'Failed to update milestone status');
      if (selectedProject) {
        fetchProjectMilestones(selectedProject.id);
      }
    }
  };

  const handleConfirmDeleteMilestone = async () => {
    if (!deleteMilestoneTarget) return;
    const id = deleteMilestoneTarget.id;
    try {
      await api.delete(`/api/v1/milestones/${id}`);
      showToast('Milestone deleted');
      setDeleteMilestoneTarget(null);
      if (selectedProject) {
        fetchProjectMilestones(selectedProject.id);
      }
    } catch (err: any) {
      console.error('Failed to delete milestone:', err);
      showToast(err.message || 'Failed to delete milestone');
    }
  };

  const handleOpenEditMilestone = (m: Milestone) => {
    setShowAddMilestoneModal(false);
    setEditingMilestone(m);
    setMilestoneForm({
      title: m.title,
      description: m.description || '',
      due_date: m.due_date ? m.due_date.substring(0, 10) : '',
      status: m.status,
      order: m.order ?? m.sort_order ?? 0,
    });
  };

  const handleCreateTaskForProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject || !newTaskForm.title.trim()) return;
    try {
      await api.post('/api/v1/tasks', {
        project_id: selectedProject.id,
        project_name: selectedProject.name,
        title: newTaskForm.title.trim(),
        description: newTaskForm.description.trim(),
        priority: newTaskForm.priority,
        status: 'todo',
        progress_percent: 0,
        due_date: newTaskForm.due_date ? newTaskForm.due_date : null,
        milestone_id: newTaskForm.milestone_id || null,
      });
      setShowAddTaskModal(false);
      setNewTaskForm({ title: '', description: '', priority: 'medium', due_date: '', milestone_id: '' });
      showToast(`Task "${newTaskForm.title}" added to "${selectedProject.name}"!`);
      await fetchProjectTasks(selectedProject.id);
      await fetchProjects();
    } catch (e: any) {
      alert(e.message || 'Failed to add task to project');
    }
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject) return;
    try {
      const updated = {
        ...editForm,
        deadline: editForm.deadline ? editForm.deadline : null,
        progress_percent: Number(editForm.progress_percent),
        platform_url: formatExternalUrl(editForm.platform_url) || null,
        platform_order_id: editForm.platform_order_id ? editForm.platform_order_id.trim() : null,
        client_username: editForm.client_username ? editForm.client_username.trim() : null,
      };
      const res = await api.patch(`/api/v1/projects/${selectedProject.id}`, updated);
      const saved = res?.data || { ...selectedProject, ...updated };
      setSelectedProject(saved);
      setProjects((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
      setIsEditing(false);
      showToast(`Project "${saved.name}" updated successfully!`);
      fetchProjects();
    } catch (e: any) {
      alert(e.message || 'Failed to update project');
    }
  };

  const confirmDeleteProject = async () => {
    if (!deleteProjectTarget) return;
    const { id: projectId, name: projectName } = deleteProjectTarget;
    try {
      await api.delete(`/api/v1/projects/${projectId}`);
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      if (selectedProject?.id === projectId) {
        handleCloseDetail();
      }
      setDeleteProjectTarget(null);
      showToast(`Project "${projectName}" deleted successfully.`);
      fetchProjects();
    } catch (e: any) {
      alert(e.message || 'Failed to delete project');
    }
  };

  return (
    <PullToRefresh onRefresh={fetchProjects}>
      <div className="space-y-6">
        {/* Toast Feedback */}
      {toastMessage && (
        <div className="fixed top-16 right-6 z-50 bg-brand-navy text-white px-4 py-2.5 rounded-lg shadow-xl text-xs flex items-center space-x-2 animate-in fade-in slide-in-from-top-2">
          <Check className="h-4 w-4 text-brand-teal" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="helper-text mt-1">Track client deliverables, derived task progress, and milestones.</p>
        </div>
        {canManageProjects && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowImportModal(true)}
              className="flex items-center space-x-1.5 font-semibold text-brand-teal border-brand-teal/40 hover:bg-brand-teal/10"
              title="Smart 1-Click Multi-Channel Order Importer"
            >
              <Sparkles className="h-4 w-4 text-brand-teal" />
              <span>Quick-Paste / Import Order</span>
            </Button>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-1.5 font-semibold"
            >
              <Plus className="h-4 w-4" />
              <span>New Project</span>
            </Button>
          </div>
        )}
      </div>

      {/* Multi-Platform Pipeline & Automated Net Profit Engine */}
      {canManageProjects && (
        <PlatformProfitabilityCard
          projects={projects}
          invoices={invoices}
          compact={false}
          title="Unified Channel Delivery Matrix & Net Profit Engine"
        />
      )}

      {/* Channel Delivery Stream Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 pt-1 border-b border-slate-200 dark:border-brand-border flex-nowrap scrollbar-none">
        {[
          { key: 'all', label: 'All Streams', count: channelCounts.all, dot: 'bg-slate-400' },
          { key: 'fiverr', label: 'Fiverr Stream', count: channelCounts.fiverr, dot: 'bg-[#1dbf73]' },
          { key: 'upwork', label: 'Upwork Stream', count: channelCounts.upwork, dot: 'bg-[#14a800]' },
          { key: 'direct', label: 'Direct Stream', count: channelCounts.direct, dot: 'bg-indigo-500' },
          { key: 'internal', label: 'Internal Products', count: channelCounts.internal, dot: 'bg-purple-500' },
        ].map((tab) => {
          const isActive = platformTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                setPlatformTab(tab.key as any);
                setPlatformFilter(tab.key === 'all' ? '' : tab.key);
                setCurrentPage(1);
              }}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap cursor-pointer shrink-0 ${
                isActive
                  ? 'bg-brand-navy text-white shadow-xs dark:bg-brand-teal dark:text-brand-navy font-bold ring-1 ring-brand-navy/20'
                  : 'bg-white dark:bg-[#12211F] text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-brand-border'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${tab.dot}`} />
              <span>{tab.label}</span>
              <span
                className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono ${
                  isActive
                    ? 'bg-white/20 text-white dark:bg-black/20 dark:text-brand-navy font-bold'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-500'
                }`}
              >
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Filter Toolbar & Advanced Filters */}
      <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 bg-white dark:bg-[#12211F] p-3 rounded-lg border border-slate-200 dark:border-brand-border shadow-2xs">
        <div className="flex flex-wrap items-center gap-2 flex-1">
          <div className="relative flex-1 min-w-[200px] sm:min-w-[240px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search project, client, @handle, order ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 text-xs"
            />
          </div>

          {/* Platform Filter Dropdown */}
          <select
            value={platformFilter}
            onChange={(e) => {
              const val = e.target.value;
              setPlatformFilter(val);
              if (['fiverr', 'upwork', 'direct', 'internal'].includes(val)) {
                setPlatformTab(val as any);
              } else if (!val) {
                setPlatformTab('all');
              }
              setCurrentPage(1);
            }}
            className="h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200"
            title="Filter by platform"
          >
            <option value="">All Platforms</option>
            <option value="fiverr">Fiverr</option>
            <option value="upwork">Upwork</option>
            <option value="direct">Direct Client</option>
            <option value="internal">Internal</option>
            <option value="other">Other</option>
          </select>

          {/* Status Filter Dropdown */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="in_progress">In Progress</option>
            <option value="in_review">In Review</option>
            <option value="at_risk">At Risk</option>
            <option value="on_hold">On Hold</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          {/* Priority Filter Dropdown */}
          <select
            value={priorityFilter}
            onChange={(e) => {
              setPriorityFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200"
          >
            <option value="">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>

          {/* Category Filter Dropdown */}
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200"
          >
            <option value="">All Categories</option>
            <option value="WordPress">WordPress</option>
            <option value="WooCommerce">WooCommerce</option>
            <option value="Custom Plugin">Custom Plugin</option>
            <option value="API Integration">API Integration</option>
            <option value="SaaS">SaaS</option>
            <option value="SEO">SEO</option>
            <option value="Mobile App">Mobile App</option>
            <option value="Maintenance">Maintenance</option>
            <option value="Other">Other</option>
          </select>

          {/* Reset Filters Button */}
          {hasActiveFilters && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleResetFilters}
              className="h-9 text-xs text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-900/50 hover:bg-rose-50 dark:hover:bg-rose-950/30 flex items-center space-x-1"
              title="Reset all filters"
            >
              <RotateCcw className="h-3 w-3" />
              <span>Reset Filters</span>
            </Button>
          )}
        </div>

        <div className="flex items-center space-x-1 self-end lg:self-center">
          <Button
            size="icon"
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            onClick={() => setViewMode('list')}
            className="h-8 w-8"
            title="List View"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            onClick={() => setViewMode('grid')}
            className="h-8 w-8"
            title="Grid View"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Projects List / Grid */}
      {loading ? (
        viewMode === 'list' ? (
          <SkeletonTable rows={6} cols={6} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )
      ) : filteredProjects.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-slate-500">
            No projects found matching current criteria.
          </CardContent>
        </Card>
      ) : viewMode === 'list' ? (
        <div className="space-y-3">
          {/* Desktop Table View */}
          <div className="hidden md:block bg-white dark:bg-[#12211F] rounded-lg border border-slate-200 dark:border-brand-border overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-brand-border bg-slate-50/75 dark:bg-[#0E1A18]">
                    <th className="py-2.5 px-4">
                      <SortButton label="Project Name" field="name" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
                    </th>
                    <th className="py-2.5 px-4">
                      <SortButton label="Client" field="client_name" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
                    </th>
                    <th className="py-2.5 px-4">
                      <SortButton label="Platform" field="platform" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
                    </th>
                    <th className="py-2.5 px-4">
                      <SortButton label="Category" field="category" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
                    </th>
                    <th className="py-2.5 px-4">
                      <SortButton label="Status" field="status" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
                    </th>
                    <th className="py-2.5 px-4">
                      <SortButton label="Priority" field="priority" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
                    </th>
                    <th className="py-2.5 px-4">
                      <SortButton label="Progress" field="progress_percent" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
                    </th>
                    <th className="py-2.5 px-4">
                      <SortButton label="Deadline & Delivery" field="deadline" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
                    </th>
                    {isOwner && <th className="py-2.5 px-4 text-right text-xs font-semibold text-slate-600 dark:text-slate-300">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-brand-border text-xs">
                  {paginatedProjects.map((proj) => (
                    <tr
                      key={proj.id}
                      onClick={() => handleOpenDetail(proj)}
                      className="hover:bg-slate-50/80 dark:hover:bg-[#162A27] transition-colors cursor-pointer group"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-1.5">
                          <span className="font-semibold text-sm text-brand-navy dark:text-brand-ink group-hover:text-brand-teal transition-colors">
                            {proj.name}
                          </span>
                          {formatExternalUrl(proj.platform_url) && (
                            <a
                              href={formatExternalUrl(proj.platform_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-slate-400 hover:text-brand-teal transition-colors p-0.5 rounded"
                              title={`Open order link: ${proj.platform_url}`}
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-400 mt-0.5">
                          <span>{proj.completed_tasks || 0}/{proj.total_tasks || 0} tasks</span>
                          {proj.platform_order_id && (
                            <span className="font-mono text-[10px] bg-slate-100 dark:bg-slate-800 px-1 py-0.2 rounded text-slate-600 dark:text-slate-300">
                              #{proj.platform_order_id}
                            </span>
                          )}
                          {proj.fiverr_revisions_remaining !== undefined && proj.fiverr_revisions_remaining !== null && (
                            <span className="text-[10px] text-emerald-600 font-medium">
                              {proj.fiverr_revisions_remaining} revs left
                            </span>
                          )}
                          {proj.fiverr_order_status && (
                            <span className="text-[10px] text-teal-600 font-medium capitalize">
                              Order: {proj.fiverr_order_status.replace('_', ' ')}
                            </span>
                          )}
                          {proj.upwork_escrow_status && (
                            <span className="text-[10px] text-sky-600 font-medium capitalize">
                              Escrow: {proj.upwork_escrow_status}
                            </span>
                          )}
                          {proj.upwork_weekly_limit_hours && (
                            <span className="text-[10px] text-indigo-600 font-medium">
                              Limit: {proj.upwork_weekly_limit_hours}h/wk
                            </span>
                          )}
                          {proj.direct_invoice_status && (
                            <span className="text-[10px] text-purple-600 font-medium capitalize">
                              Invoice: {proj.direct_invoice_status}
                            </span>
                          )}
                          {proj.portal_token && (
                            <Link
                              to={`/portal?token=${proj.portal_token}`}
                              onClick={(e) => e.stopPropagation()}
                              target="_blank"
                              className="text-[10px] text-brand-teal hover:underline font-medium"
                              title="Open Client Portal"
                            >
                              Portal Link ↗
                            </Link>
                          )}
                        </div>
                        {proj.budget !== undefined && proj.budget !== null && Number(proj.budget) > 0 && (
                          <div className="flex items-center space-x-2 text-[11px] font-mono mt-1 text-slate-600 dark:text-slate-300">
                            <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                              ${Number(proj.budget).toLocaleString()} gross → ${calculatePlatformCut(proj.budget, proj.platform).netRevenue.toLocaleString()} net ({calculatePlatformCut(proj.budget, proj.platform).marginPercent}%)
                            </span>
                            {proj.recorded_hours && Number(proj.recorded_hours) > 0 ? (
                              <span className="bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/50 px-1.5 py-0.2 rounded text-[10px] font-bold">
                                ${calculateEffectiveHourlyYield(calculatePlatformCut(proj.budget, proj.platform).netRevenue, proj.recorded_hours)}/hr
                              </span>
                            ) : null}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 font-medium text-slate-700 dark:text-slate-300">
                        <div>{proj.client_name || 'Internal'}</div>
                        {proj.client_username && (
                          <div className="text-[10px] text-slate-400 font-mono">@{proj.client_username}</div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <PlatformBadge platform={proj.platform} size="xs" />
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="secondary" className="text-[10px]">
                          {proj.category}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-1.5">
                          <Badge variant={proj.status as any} className="text-[10px] capitalize">
                            {proj.status.replace('_', ' ')}
                          </Badge>
                          {proj.progress_mode === 'manual' && (
                            <span className="text-[10px] bg-amber-50 text-amber-800 border border-amber-200 px-1 py-0.5 rounded font-medium">
                              Manual
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={proj.priority as any} className="text-[10px] uppercase">
                          {proj.priority}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2 w-32">
                          <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-1.5 overflow-hidden">
                            <div
                              className="bg-brand-teal h-full rounded-full transition-all"
                              style={{ width: `${proj.progress_percent}%` }}
                            />
                          </div>
                          <span className="text-[11px] font-mono font-semibold text-slate-700 dark:text-slate-300 w-8 text-right">
                            {proj.progress_percent}%
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-500 whitespace-nowrap">
                        <div className="space-y-1">
                          <DeliveryCountdownBadge deadline={proj.deadline} status={proj.status} size="xs" />
                          <div className="text-[10px] text-slate-400">
                            {proj.deadline ? new Date(proj.deadline).toLocaleDateString() : 'No deadline'}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end space-x-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeliveryModalProject(proj);
                            }}
                            className="h-7 text-[11px] px-2 border-brand-teal/40 text-brand-teal hover:bg-brand-teal/10 font-semibold"
                            title={`Generate ${proj.platform || 'Channel'} Handover / Delivery Note`}
                          >
                            <Send className="w-3 h-3 mr-1" />
                            <span>Handover</span>
                          </Button>
                          {isOwner && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteProjectTarget(proj);
                              }}
                              className="h-7 w-7 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Delete Project"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Stacked Card View (360px safe, 0 horizontal scroll) */}
          <div className="md:hidden space-y-3">
            {paginatedProjects.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-500 bg-white dark:bg-[#12211F] rounded-xl border border-slate-200 dark:border-brand-border">
                No projects found.
              </div>
            ) : (
              paginatedProjects.map((proj) => (
                <div
                  key={proj.id}
                  onClick={() => handleOpenDetail(proj)}
                  className="p-3.5 rounded-xl bg-white dark:bg-[#12211F] border border-slate-200 dark:border-brand-border space-y-2 cursor-pointer shadow-xs active:scale-[0.99] transition-transform"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1.5 truncate max-w-[180px]">
                      <PlatformBadge platform={proj.platform} size="xs" />
                      <span className="text-[10px] font-bold text-brand-teal uppercase truncate">
                        {proj.category}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                      <DeliveryCountdownBadge deadline={proj.deadline} status={proj.status} size="xs" />
                      <Badge variant={proj.status as any} className="text-[9px] capitalize px-1.5 py-0">
                        {proj.status.replace('_', ' ')}
                      </Badge>
                      <Badge variant={proj.priority as any} className="text-[9px] uppercase px-1 py-0">
                        {proj.priority}
                      </Badge>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold text-brand-navy dark:text-brand-ink leading-snug">
                      {proj.name}
                    </h4>
                    {formatExternalUrl(proj.platform_url) && (
                      <a
                        href={formatExternalUrl(proj.platform_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-slate-400 hover:text-brand-teal p-1"
                        title="Open external order"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>

                  <div className="text-xs text-slate-600 dark:text-brand-muted flex items-center justify-between">
                    <div className="flex items-center space-x-1 truncate">
                      <span>{proj.client_name || 'Internal'}</span>
                      {proj.client_username && (
                        <span className="text-slate-400 text-[10px] font-mono">(@{proj.client_username})</span>
                      )}
                    </div>
                    <span className="text-[11px] text-slate-400 shrink-0">
                      {proj.completed_tasks || 0}/{proj.total_tasks || 0} tasks
                    </span>
                  </div>

                  <div className="flex items-center space-x-2 pt-1">
                    <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="bg-brand-teal h-full rounded-full transition-all"
                        style={{ width: `${proj.progress_percent}%` }}
                      />
                    </div>
                    <span className="text-[11px] font-mono font-semibold text-slate-700 dark:text-slate-300 w-8 text-right">
                      {proj.progress_percent}%
                    </span>
                  </div>

                  {proj.budget !== undefined && proj.budget !== null && Number(proj.budget) > 0 && (
                    <div className="flex items-center justify-between text-[11px] font-mono pt-1">
                      <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                        ${Number(proj.budget).toLocaleString()} → ${calculatePlatformCut(proj.budget, proj.platform).netRevenue.toLocaleString()} net
                      </span>
                      {proj.recorded_hours && Number(proj.recorded_hours) > 0 ? (
                        <span className="text-[10px] bg-slate-100 dark:bg-slate-800 px-1 rounded text-slate-700 dark:text-slate-300">
                          ${calculateEffectiveHourlyYield(calculatePlatformCut(proj.budget, proj.platform).netRevenue, proj.recorded_hours)}/hr
                        </span>
                      ) : null}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-1 text-[11px] text-slate-500 border-t border-slate-100 dark:border-brand-border/60">
                    <div className="flex items-center space-x-2">
                      <span>
                        {proj.deadline ? `Due ${new Date(proj.deadline).toLocaleDateString()}` : 'No deadline'}
                      </span>
                      {proj.platform_order_id && (
                        <span className="font-mono text-[10px] bg-slate-100 dark:bg-slate-800 px-1 rounded text-slate-600 dark:text-slate-300">
                          #{proj.platform_order_id}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeliveryModalProject(proj);
                        }}
                        className="h-6 text-[10px] px-2 border-brand-teal/40 text-brand-teal hover:bg-brand-teal/10"
                      >
                        <Send className="w-2.5 h-2.5 mr-1" />
                        <span>Handover</span>
                      </Button>
                      {isOwner && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteProjectTarget(proj);
                          }}
                          className="h-6 w-6 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete Project"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <Pagination
            totalItems={sortedProjects.length}
            currentPage={validCurrentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedProjects.map((proj) => (
              <Card
                key={proj.id}
                onClick={() => handleOpenDetail(proj)}
                className="hover:border-brand-teal/50 transition-all flex flex-col justify-between cursor-pointer group shadow-2xs hover:shadow-xs"
              >
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1.5">
                      <PlatformBadge platform={proj.platform} size="xs" />
                      <Badge variant={proj.status as any} className="text-[10px] capitalize">
                        {proj.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-1">
                      <DeliveryCountdownBadge deadline={proj.deadline} status={proj.status} size="xs" />
                      <span className="text-xs text-slate-400">{proj.category}</span>
                      {isOwner && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteProjectTarget(proj);
                          }}
                          className="h-7 w-7 text-slate-300 hover:text-red-600 hover:bg-red-50"
                          title="Delete Project"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-base text-brand-navy group-hover:text-brand-teal transition-colors line-clamp-1">
                      {proj.name}
                    </h3>
                    {formatExternalUrl(proj.platform_url) && (
                      <a
                        href={formatExternalUrl(proj.platform_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-slate-400 hover:text-brand-teal p-1 ml-1 shrink-0"
                        title="Open external order"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 flex items-center justify-between">
                    <div className="truncate">
                      <span>Client: {proj.client_name || 'Internal'}</span>
                      {proj.client_username && (
                        <span className="text-slate-400 font-mono ml-1">(@{proj.client_username})</span>
                      )}
                    </div>
                    <span className="shrink-0">{proj.completed_tasks || 0} of {proj.total_tasks || 0} tasks</span>
                  </div>

                  {proj.platform_order_id && (
                    <div className="text-[11px] font-mono text-slate-500 bg-slate-50 dark:bg-slate-800/60 px-2 py-0.5 rounded border border-slate-100 dark:border-slate-700/50 inline-block">
                      Order: #{proj.platform_order_id}
                    </div>
                  )}

                  {/* Progress Bar */}
                  <div className="space-y-1 pt-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">
                        Progress {proj.progress_mode === 'manual' && '(Manual)'}
                      </span>
                      <span className="font-mono font-bold text-slate-800">{proj.progress_percent}%</span>
                    </div>
                    <div className="bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-brand-teal h-full rounded-full transition-all"
                        style={{ width: `${proj.progress_percent}%` }}
                      />
                    </div>
                  </div>

                  {proj.budget !== undefined && proj.budget !== null && Number(proj.budget) > 0 && (
                    <div className="flex items-center justify-between text-xs font-mono pt-1">
                      <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                        ${Number(proj.budget).toLocaleString()} → ${calculatePlatformCut(proj.budget, proj.platform).netRevenue.toLocaleString()} net
                      </span>
                      {proj.recorded_hours && Number(proj.recorded_hours) > 0 ? (
                        <span className="text-[10px] bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-slate-700 dark:text-slate-300">
                          ${calculateEffectiveHourlyYield(calculatePlatformCut(proj.budget, proj.platform).netRevenue, proj.recorded_hours)}/hr
                        </span>
                      ) : null}
                    </div>
                  )}

                  {/* Channel Delivery Actions */}
                  <div className="pt-2 flex items-center justify-between border-t border-slate-100 dark:border-brand-border/60">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeliveryModalProject(proj);
                      }}
                      className="h-7 text-xs px-2.5 border-brand-teal/40 text-brand-teal hover:bg-brand-teal/10 font-semibold"
                    >
                      <Send className="w-3 h-3 mr-1" />
                      <span>Delivery & Handover</span>
                    </Button>
                    <div className="flex flex-wrap items-center gap-1.5 justify-end">
                      {proj.fiverr_revisions_remaining !== undefined && proj.fiverr_revisions_remaining !== null && (
                        <span className="text-[10px] text-emerald-600 font-medium bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.2 rounded border border-emerald-200 dark:border-emerald-800/50">
                          {proj.fiverr_revisions_remaining} revs left
                        </span>
                      )}
                      {proj.fiverr_order_status && (
                        <span className="text-[10px] text-teal-600 font-medium capitalize bg-teal-50 dark:bg-teal-950/40 px-1.5 py-0.2 rounded border border-teal-200 dark:border-teal-800/50">
                          {proj.fiverr_order_status.replace('_', ' ')}
                        </span>
                      )}
                      {proj.upwork_escrow_status && (
                        <span className="text-[10px] text-sky-600 font-medium capitalize bg-sky-50 dark:bg-sky-950/40 px-1.5 py-0.2 rounded border border-sky-200 dark:border-sky-800/50">
                          Escrow: {proj.upwork_escrow_status}
                        </span>
                      )}
                      {proj.upwork_weekly_limit_hours && (
                        <span className="text-[10px] text-indigo-600 font-medium bg-indigo-50 dark:bg-indigo-950/40 px-1.5 py-0.2 rounded border border-indigo-200 dark:border-indigo-800/50">
                          {proj.upwork_weekly_limit_hours}h/wk
                        </span>
                      )}
                      {proj.direct_invoice_status && (
                        <span className="text-[10px] text-purple-600 font-medium capitalize bg-purple-50 dark:bg-purple-950/40 px-1.5 py-0.2 rounded border border-purple-200 dark:border-purple-800/50">
                          Invoice: {proj.direct_invoice_status}
                        </span>
                      )}
                      {proj.portal_token && (
                        <Link
                          to={`/portal?token=${proj.portal_token}`}
                          onClick={(e) => e.stopPropagation()}
                          target="_blank"
                          className="text-[10px] text-brand-teal hover:underline font-medium bg-teal-50 dark:bg-teal-950/40 px-1.5 py-0.2 rounded border border-teal-200 dark:border-teal-800/50"
                        >
                          Portal ↗
                        </Link>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <Pagination
            totalItems={sortedProjects.length}
            currentPage={validCurrentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Project Detail & Edit Drawer / Modal */}
      {selectedProject && (
        <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col border-l border-slate-200">
          {/* Drawer Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50/50">
            <div className="flex items-center space-x-2">
              <Badge variant={selectedProject.status as any} className="capitalize text-xs">
                {selectedProject.status.replace('_', ' ')}
              </Badge>
              <Badge variant="secondary" className="text-xs">
                {selectedProject.category}
              </Badge>
            </div>
            <div className="flex items-center space-x-1">
              {canManageProjects && (
                <Button
                  size="sm"
                  variant={isEditing ? 'secondary' : 'outline'}
                  onClick={() => setIsEditing(!isEditing)}
                  className="text-xs h-8"
                >
                  <Edit3 className="h-3.5 w-3.5 mr-1" />
                  <span>{isEditing ? 'Cancel Edit' : 'Edit Project'}</span>
                </Button>
              )}
              <button
                onClick={handleCloseDetail}
                className="text-slate-400 hover:text-slate-700 p-1.5 rounded-md"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Drawer Tabs */}
          {!isEditing && (
            <div className="flex items-center border-b border-slate-200 bg-slate-50/50 px-6 pt-2 space-x-6 text-xs font-semibold shrink-0">
              <button
                type="button"
                onClick={() => setActiveTab('overview')}
                className={`pb-2.5 border-b-2 transition-colors flex items-center space-x-1.5 ${
                  activeTab === 'overview'
                    ? 'border-brand-teal text-brand-teal font-bold'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <span>Overview</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('tasks')}
                className={`pb-2.5 border-b-2 transition-colors flex items-center space-x-1.5 ${
                  activeTab === 'tasks'
                    ? 'border-brand-teal text-brand-teal font-bold'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <CheckSquare className="h-3.5 w-3.5" />
                <span>Tasks</span>
                <span className="bg-slate-200 text-slate-700 px-1.5 py-0.2 rounded-full text-[10px]">
                  {projectTasks.length}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('milestones')}
                className={`pb-2.5 border-b-2 transition-colors flex items-center space-x-1.5 ${
                  activeTab === 'milestones'
                    ? 'border-brand-teal text-brand-teal font-bold'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Flag className="h-3.5 w-3.5" />
                <span>Milestones</span>
                <span className="bg-teal-100 text-brand-teal px-1.5 py-0.2 rounded-full text-[10px] font-bold">
                  {projectMilestones.length}
                </span>
              </button>
            </div>
          )}

          {/* Drawer Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {!isEditing ? (
              <>
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center space-x-2">
                          <PlatformBadge platform={selectedProject.platform} size="sm" />
                          <DeliveryCountdownBadge deadline={selectedProject.deadline} status={selectedProject.status} size="sm" />
                        </div>
                        <h2 className="text-xl font-bold text-brand-navy mt-2">{selectedProject.name}</h2>
                        <div className="flex items-center space-x-2 text-xs text-slate-400 font-mono mt-0.5">
                          <span>Slug: {selectedProject.slug}</span>
                          {selectedProject.platform_order_id && (
                            <span className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-slate-600 dark:text-slate-300">
                              Order #{selectedProject.platform_order_id}
                            </span>
                          )}
                        </div>
                      </div>
                      {formatExternalUrl(selectedProject.platform_url) && (
                        <a
                          href={formatExternalUrl(selectedProject.platform_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-md bg-brand-teal/10 hover:bg-brand-teal/20 text-brand-teal text-xs font-semibold transition-colors shrink-0"
                          title="Open external order in new tab"
                        >
                          <span>Launch Order</span>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>

                    {/* Scope Notes & Description */}
                    <div className="space-y-1.5 p-4 rounded-lg bg-slate-50 border border-slate-100 text-xs">
                      <span className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">
                        Scope Notes & Description
                      </span>
                      <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                        {selectedProject.description || 'No scope notes or description provided for this project.'}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-slate-50 border border-slate-100 text-xs">
                      <div>
                        <span className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Client</span>
                        <div className="font-semibold text-slate-800 mt-0.5 flex items-center space-x-1">
                          <span>{selectedProject.client_name || 'Internal'}</span>
                          {selectedProject.client_username && (
                            <span className="text-slate-500 font-mono text-[11px]">(@{selectedProject.client_username})</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Platform Channel</span>
                        <div className="mt-1">
                          <PlatformBadge platform={selectedProject.platform} size="xs" />
                        </div>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Priority</span>
                        <div className="mt-0.5">
                          <Badge variant={selectedProject.priority as any} className="uppercase text-[9px]">
                            {selectedProject.priority}
                          </Badge>
                        </div>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Deadline</span>
                        <div className="font-semibold text-slate-800 mt-0.5 flex items-center space-x-1.5">
                          <span>{selectedProject.deadline ? new Date(selectedProject.deadline).toLocaleDateString() : 'No due date'}</span>
                          <DeliveryCountdownBadge deadline={selectedProject.deadline} status={selectedProject.status} size="xs" />
                        </div>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Tasks Completed</span>
                        <div className="font-semibold text-slate-800 mt-0.5">
                          {selectedProject.completed_tasks || projectTasks.filter(t => t.status === 'done').length} of {selectedProject.total_tasks || projectTasks.length} tasks
                        </div>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Order / Contract ID</span>
                        <div className="font-mono text-slate-800 mt-0.5">
                          {selectedProject.platform_order_id ? `#${selectedProject.platform_order_id}` : '—'}
                        </div>
                      </div>
                    </div>

                    {/* Progress Derivation Section */}
                    <div className="space-y-2 border-t border-slate-100 pt-4">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-slate-700">Project Progress</span>
                        <span className="font-mono font-bold text-slate-800">{selectedProject.progress_percent}%</span>
                      </div>
                      <div className="bg-slate-100 rounded-full h-2.5 overflow-hidden">
                        <div
                          className="bg-brand-teal h-full rounded-full transition-all"
                          style={{ width: `${selectedProject.progress_percent}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1">
                        <span>
                          Mode: <strong className="capitalize text-slate-700">{selectedProject.progress_mode || 'automatic'}</strong>
                        </span>
                        {selectedProject.progress_mode === 'manual' && (
                          <span className="text-amber-700 italic">
                            Reason: {selectedProject.manual_override_reason || 'Manual manager override'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Milestones Roadmap Summary Widget */}
                    <div className="space-y-2 border-t border-slate-100 pt-4">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center space-x-1.5">
                          <Flag className="h-4 w-4 text-brand-teal" />
                          <span>Milestones Roadmap ({projectMilestones.length})</span>
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setActiveTab('milestones')}
                          className="text-xs h-7 text-brand-teal hover:bg-teal-50 flex items-center space-x-1"
                        >
                          <span>Manage Milestones</span>
                          <ArrowRight className="h-3.5 w-3.5 ml-1" />
                        </Button>
                      </div>

                      {projectMilestones.length === 0 ? (
                        <div className="text-xs text-slate-400 py-3 text-center border border-dashed border-slate-200 rounded-lg">
                          No milestones defined yet. Click "Manage Milestones" to create key deliverable milestones.
                        </div>
                      ) : (
                        <div className="p-3 bg-slate-50 border border-slate-200/80 rounded-lg space-y-2.5">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-600 font-medium">Completion Progress</span>
                            <span className="font-mono font-bold text-slate-800">
                              {projectMilestones.filter((m) => m.status === 'completed').length} of {projectMilestones.length} (
                              {Math.round(
                                (projectMilestones.filter((m) => m.status === 'completed').length /
                                  projectMilestones.length) *
                                  100
                              )}
                              %)
                            </span>
                          </div>
                          <div className="bg-slate-200 rounded-full h-2 overflow-hidden">
                            <div
                              className="bg-brand-teal h-full rounded-full transition-all"
                              style={{
                                width: `${Math.round(
                                  (projectMilestones.filter((m) => m.status === 'completed').length /
                                    projectMilestones.length) *
                                    100
                                )}%`,
                              }}
                            />
                          </div>
                          <div className="space-y-1.5 pt-1">
                            {projectMilestones.slice(0, 3).map((m, idx) => (
                              <div
                                key={m.id}
                                className="flex items-center justify-between text-xs py-1 px-2 rounded bg-white border border-slate-100"
                              >
                                <div className="flex items-center space-x-2 truncate">
                                  <span className="text-[10px] font-mono font-semibold text-slate-400">
                                    #{m.order ?? m.sort_order ?? idx + 1}
                                  </span>
                                  <span className="truncate font-medium text-slate-700">{m.title}</span>
                                </div>
                                <Badge
                                  variant={m.status === 'completed' ? 'completed' : m.status === 'in_progress' ? 'in_progress' : 'secondary'}
                                  className="text-[9px] capitalize px-1.5 py-0"
                                >
                                  {m.status.replace('_', ' ')}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Quick Tasks preview */}
                    <div className="space-y-2 border-t border-slate-100 pt-4">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center space-x-1.5">
                          <CheckSquare className="h-4 w-4 text-brand-teal" />
                          <span>Tasks Overview ({projectTasks.length})</span>
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setActiveTab('tasks')}
                          className="text-xs h-7 text-brand-teal hover:bg-teal-50 flex items-center space-x-1"
                        >
                          <span>Manage Tasks</span>
                          <ArrowRight className="h-3.5 w-3.5 ml-1" />
                        </Button>
                      </div>
                      <div className="space-y-1">
                        {projectTasks.slice(0, 3).map((t) => (
                          <div
                            key={t.id}
                            onClick={() => navigate(`/tasks/${t.id}`)}
                            className="flex items-center justify-between p-2 rounded-md hover:bg-slate-50 border border-slate-100 transition-colors cursor-pointer group"
                          >
                            <span className="text-xs font-medium text-slate-800 group-hover:text-brand-teal truncate">
                              {t.title}
                            </span>
                            <Badge variant={t.status as any} className="text-[9px] capitalize px-1.5 py-0">
                              {t.status.replace('_', ' ')}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Tasks Tab */}
                {activeTab === 'tasks' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center space-x-1.5">
                        <CheckSquare className="h-4 w-4 text-brand-teal" />
                        <span>Project Tasks ({projectTasks.length})</span>
                      </span>
                      <div className="flex items-center space-x-2">
                        <select
                          value={projectTaskMilestoneFilter}
                          onChange={(e) => setProjectTaskMilestoneFilter(e.target.value)}
                          className="text-xs h-7 px-2 rounded-md border border-slate-200 bg-white text-slate-700 font-medium"
                        >
                          <option value="all">All Milestones</option>
                          <option value="unlinked">No milestone (Unlinked)</option>
                          {projectMilestones.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.title}
                            </option>
                          ))}
                        </select>
                        {!isViewer && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowAddTaskModal(!showAddTaskModal)}
                            className="text-xs h-7 px-2 border-brand-teal text-brand-teal hover:bg-teal-50"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            <span>{showAddTaskModal ? 'Cancel' : 'Add Task'}</span>
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Add Task inline Form */}
                    {showAddTaskModal && (
                      <form onSubmit={handleCreateTaskForProject} className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-2 text-xs animate-in fade-in">
                        <div className="font-semibold text-brand-navy">Create Task in {selectedProject.name}</div>
                        <div>
                          <label className="text-[11px] text-slate-600 font-medium">Task Title *</label>
                          <Input
                            required
                            placeholder="e.g. Set up staging SSL and redirect rules"
                            value={newTaskForm.title}
                            onChange={(e) => setNewTaskForm({ ...newTaskForm, title: e.target.value })}
                            className="mt-0.5 text-xs h-8"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] text-slate-600 font-medium">Description</label>
                          <textarea
                            rows={2}
                            placeholder="Task acceptance criteria..."
                            value={newTaskForm.description}
                            onChange={(e) => setNewTaskForm({ ...newTaskForm, description: e.target.value })}
                            className="w-full mt-0.5 rounded-md border border-slate-200 bg-white p-2 text-xs"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-[11px] text-slate-600 font-medium">Milestone Deliverable</label>
                            <select
                              value={newTaskForm.milestone_id}
                              onChange={(e) => setNewTaskForm({ ...newTaskForm, milestone_id: e.target.value })}
                              className="w-full mt-0.5 h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"
                            >
                              <option value="">None (Independent Task)</option>
                              {projectMilestones.map((m) => (
                                <option key={m.id} value={m.id}>
                                  {m.title}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-[11px] text-slate-600 font-medium">Priority</label>
                            <select
                              value={newTaskForm.priority}
                              onChange={(e) => setNewTaskForm({ ...newTaskForm, priority: e.target.value })}
                              className="w-full mt-0.5 h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"
                            >
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                              <option value="urgent">Urgent</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-[11px] text-slate-600 font-medium">Due Date (Optional)</label>
                            <Input
                              type="date"
                              value={newTaskForm.due_date}
                              onChange={(e) => setNewTaskForm({ ...newTaskForm, due_date: e.target.value })}
                              className="mt-0.5 text-xs h-8"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end space-x-2 pt-1">
                          <Button type="button" variant="ghost" size="sm" onClick={() => setShowAddTaskModal(false)} className="text-xs h-7">
                            Cancel
                          </Button>
                          <Button type="submit" size="sm" className="bg-brand-teal text-white text-xs h-7">
                            Create Task
                          </Button>
                        </div>
                      </form>
                    )}

                    {/* Task list */}
                    <div className="space-y-1.5 max-h-[460px] overflow-y-auto">
                      {(() => {
                        const filtered = projectTasks.filter((t) => {
                          if (projectTaskMilestoneFilter !== 'all') {
                            if (projectTaskMilestoneFilter === 'unlinked') {
                              if (t.milestone_id) return false;
                            } else if (t.milestone_id !== projectTaskMilestoneFilter && String(t.milestone_id) !== String(projectTaskMilestoneFilter)) {
                              return false;
                            }
                          }
                          return true;
                        });

                        if (filtered.length === 0) {
                          return (
                            <div className="text-xs text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-lg">
                              {projectTasks.length === 0 ? 'No tasks created for this project yet. Click "Add Task" to create one.' : 'No tasks match the selected milestone filter.'}
                            </div>
                          );
                        }

                        return filtered.map((t) => (
                          <div
                            key={t.id}
                            onClick={() => {
                              navigate(`/tasks/${t.id}`);
                            }}
                            className="flex items-center justify-between p-2.5 rounded-md hover:bg-slate-50 border border-slate-100 transition-colors cursor-pointer group"
                          >
                            <div className="flex items-center space-x-2 min-w-0 flex-1">
                              <span className="text-xs font-medium text-slate-800 group-hover:text-brand-teal truncate">
                                {t.title}
                              </span>
                              {t.milestone_id && (() => {
                                const tm = projectMilestones.find((m) => m.id === t.milestone_id || String(m.id) === String(t.milestone_id));
                                return tm ? (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-brand-teal/10 text-brand-teal border border-brand-teal/20 shrink-0" title={tm.title}>
                                    <Flag className="w-2.5 h-2.5 shrink-0" />
                                    <span className="truncate max-w-[120px]">{tm.title}</span>
                                  </span>
                                ) : null;
                              })()}
                            </div>
                            <div className="flex items-center space-x-2 shrink-0">
                              <Badge variant={t.status as any} className="text-[9px] capitalize px-1.5 py-0">
                                {t.status.replace('_', ' ')}
                              </Badge>
                              <Badge variant={t.priority as any} className="text-[9px] uppercase px-1.5 py-0">
                                {t.priority}
                              </Badge>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>

                    <div className="border-t border-slate-100 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigate('/tasks');
                        }}
                        className="w-full text-xs flex items-center justify-center space-x-1"
                      >
                        <span>View All Tasks in Board</span>
                        <ExternalLink className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Milestones Tab */}
                {activeTab === 'milestones' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-sm text-brand-navy flex items-center space-x-1.5">
                          <Flag className="h-4 w-4 text-brand-teal" />
                          <span>Project Milestones & Deliverables</span>
                        </h3>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          Track roadmap delivery stages, deliverables, and handoffs
                        </p>
                      </div>
                      {canManageProjects && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setEditingMilestone(null);
                            setMilestoneForm({
                              title: '',
                              description: '',
                              due_date: '',
                              status: 'pending',
                              order: projectMilestones.length + 1
                            });
                            setShowAddMilestoneModal(!showAddMilestoneModal);
                          }}
                          className="bg-brand-teal text-white hover:bg-brand-teal/90 text-xs h-7 px-2.5 flex items-center space-x-1"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          <span>{showAddMilestoneModal ? 'Cancel' : 'Add Milestone'}</span>
                        </Button>
                      )}
                    </div>

                    {/* Progress Velocity Card */}
                    {(() => {
                      const allMTasks = projectTasks.filter((t) => projectMilestones.some((m) => m.id === t.milestone_id || String(m.id) === String(t.milestone_id)));
                      const completedMTasks = allMTasks.filter((t) => ['done', 'completed'].includes((t.status || '').toLowerCase()));
                      const completedMilestones = projectMilestones.filter((m) => m.status === 'completed');
                      const progressPct = allMTasks.length > 0
                        ? Math.round((completedMTasks.length / allMTasks.length) * 100)
                        : (projectMilestones.length > 0 ? Math.round((completedMilestones.length / projectMilestones.length) * 100) : 0);

                      return (
                        <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-lg space-y-2.5">
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-semibold text-slate-700">Milestone Completion Velocity</span>
                            <span className="font-mono font-bold text-slate-800">
                              {completedMilestones.length} of {projectMilestones.length} Delivered ({progressPct}%)
                              {allMTasks.length > 0 && (
                                <span className="ml-1 text-brand-teal font-semibold">
                                  • {completedMTasks.length}/{allMTasks.length} Tasks
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="bg-slate-200 rounded-full h-2 overflow-hidden">
                            <div
                              className="bg-gradient-to-r from-brand-teal to-teal-400 h-full rounded-full transition-all"
                              style={{ width: `${progressPct}%` }}
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-2 pt-1 text-center text-[11px]">
                            <div className="p-1.5 rounded bg-white border border-slate-100">
                              <span className="text-slate-400 block text-[9px] uppercase font-semibold">Completed</span>
                              <span className="font-bold text-teal-700 font-mono">
                                {completedMilestones.length}
                              </span>
                            </div>
                            <div className="p-1.5 rounded bg-white border border-slate-100">
                              <span className="text-slate-400 block text-[9px] uppercase font-semibold">In Progress</span>
                              <span className="font-bold text-blue-700 font-mono">
                                {projectMilestones.filter((m) => m.status === 'in_progress').length}
                              </span>
                            </div>
                            <div className="p-1.5 rounded bg-white border border-slate-100">
                              <span className="text-slate-400 block text-[9px] uppercase font-semibold">Pending</span>
                              <span className="font-bold text-slate-600 font-mono">
                                {projectMilestones.filter((m) => m.status === 'pending').length}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Add Milestone Inline Form */}
                    {showAddMilestoneModal && (
                      <form
                        onSubmit={handleCreateMilestone}
                        className="p-4 bg-teal-50/50 border border-teal-200 rounded-lg space-y-3 text-xs animate-in fade-in"
                      >
                        <div className="font-bold text-brand-navy flex items-center space-x-1.5">
                          <Plus className="h-3.5 w-3.5 text-brand-teal" />
                          <span>New Milestone for {selectedProject.name}</span>
                        </div>
                        <div>
                          <label className="text-[11px] font-semibold text-slate-700">Milestone Title *</label>
                          <Input
                            required
                            placeholder="e.g. Staging QA & Payment Gateway Integration"
                            value={milestoneForm.title}
                            onChange={(e) => setMilestoneForm({ ...milestoneForm, title: e.target.value })}
                            className="mt-1 text-xs h-8 bg-white"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] font-semibold text-slate-700">Scope Description</label>
                          <textarea
                            rows={2}
                            placeholder="Deliverable acceptance criteria, signoff checklist..."
                            value={milestoneForm.description}
                            onChange={(e) => setMilestoneForm({ ...milestoneForm, description: e.target.value })}
                            className="w-full mt-1 rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-800"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-[11px] font-semibold text-slate-700">Status</label>
                            <select
                              value={milestoneForm.status}
                              onChange={(e) => setMilestoneForm({ ...milestoneForm, status: e.target.value as MilestoneStatus })}
                              className="w-full mt-1 h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"
                            >
                              <option value="pending">Pending</option>
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-[11px] font-semibold text-slate-700">Due Date</label>
                            <Input
                              type="date"
                              value={milestoneForm.due_date}
                              onChange={(e) => setMilestoneForm({ ...milestoneForm, due_date: e.target.value })}
                              className="mt-1 text-xs h-8 bg-white"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-semibold text-slate-700">Order / Step #</label>
                            <Input
                              type="number"
                              min={1}
                              value={milestoneForm.order}
                              onChange={(e) => setMilestoneForm({ ...milestoneForm, order: parseInt(e.target.value) || 0 })}
                              className="mt-1 text-xs h-8 bg-white"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end space-x-2 pt-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setShowAddMilestoneModal(false);
                              setMilestoneForm({ title: '', description: '', due_date: '', status: 'pending', order: 0 });
                            }}
                            className="text-xs h-7"
                          >
                            Cancel
                          </Button>
                          <Button type="submit" size="sm" className="bg-brand-teal text-white text-xs h-7">
                            Create Milestone
                          </Button>
                        </div>
                      </form>
                    )}

                    {/* Edit Milestone Inline Form */}
                    {editingMilestone && (
                      <form
                        onSubmit={handleSaveEditMilestone}
                        className="p-4 bg-amber-50/50 border border-amber-200 rounded-lg space-y-3 text-xs animate-in fade-in"
                      >
                        <div className="font-bold text-brand-navy flex items-center justify-between">
                          <div className="flex items-center space-x-1.5">
                            <Edit3 className="h-3.5 w-3.5 text-amber-600" />
                            <span>Edit Milestone</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setEditingMilestone(null)}
                            className="text-slate-400 hover:text-slate-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        <div>
                          <label className="text-[11px] font-semibold text-slate-700">Milestone Title *</label>
                          <Input
                            required
                            value={milestoneForm.title}
                            onChange={(e) => setMilestoneForm({ ...milestoneForm, title: e.target.value })}
                            className="mt-1 text-xs h-8 bg-white"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] font-semibold text-slate-700">Scope Description</label>
                          <textarea
                            rows={2}
                            value={milestoneForm.description}
                            onChange={(e) => setMilestoneForm({ ...milestoneForm, description: e.target.value })}
                            className="w-full mt-1 rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-800"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-[11px] font-semibold text-slate-700">Status</label>
                            <select
                              value={milestoneForm.status}
                              onChange={(e) => setMilestoneForm({ ...milestoneForm, status: e.target.value as MilestoneStatus })}
                              className="w-full mt-1 h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"
                            >
                              <option value="pending">Pending</option>
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-[11px] font-semibold text-slate-700">Due Date</label>
                            <Input
                              type="date"
                              value={milestoneForm.due_date}
                              onChange={(e) => setMilestoneForm({ ...milestoneForm, due_date: e.target.value })}
                              className="mt-1 text-xs h-8 bg-white"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-semibold text-slate-700">Order / Step #</label>
                            <Input
                              type="number"
                              min={1}
                              value={milestoneForm.order}
                              onChange={(e) => setMilestoneForm({ ...milestoneForm, order: parseInt(e.target.value) || 0 })}
                              className="mt-1 text-xs h-8 bg-white"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end space-x-2 pt-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingMilestone(null);
                              setMilestoneForm({ title: '', description: '', due_date: '', status: 'pending', order: 0 });
                            }}
                            className="text-xs h-7"
                          >
                            Cancel
                          </Button>
                          <Button type="submit" size="sm" className="bg-brand-teal text-white text-xs h-7">
                            Save Changes
                          </Button>
                        </div>
                      </form>
                    )}

                    {/* Milestones List & Skeleton Loading */}
                    {loadingMilestones ? (
                      <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="p-3.5 rounded-lg border border-slate-200 bg-white space-y-2.5 animate-pulse">
                            <div className="flex items-center justify-between">
                              <Skeleton className="h-4 w-1/3" />
                              <Skeleton className="h-5 w-20 rounded-full" />
                            </div>
                            <Skeleton className="h-3 w-3/4" />
                            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                              <Skeleton className="h-3 w-28" />
                              <div className="flex space-x-2">
                                <Skeleton className="h-6 w-14 rounded" />
                                <Skeleton className="h-6 w-6 rounded" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : projectMilestones.length === 0 ? (
                      <div className="text-center py-8 px-4 border border-dashed border-slate-200 rounded-xl bg-slate-50/50 space-y-3">
                        <div className="w-10 h-10 rounded-full bg-teal-50 text-brand-teal flex items-center justify-center mx-auto">
                          <Flag className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-slate-700">No Milestones Defined Yet</p>
                          <p className="text-[11px] text-slate-400 mt-0.5">
                            Break down "{selectedProject.name}" into phased client deliverables.
                          </p>
                        </div>
                        {canManageProjects && (
                          <Button
                            size="sm"
                            onClick={() => setShowAddMilestoneModal(true)}
                            className="bg-brand-teal text-white hover:bg-brand-teal/90 text-xs h-7 px-3"
                          >
                            <Plus className="h-3.5 w-3.5 mr-1" />
                            <span>Add First Milestone</span>
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {projectMilestones.map((m, idx) => {
                          const isDone = m.status === 'completed';
                          const isOngoing = m.status === 'in_progress';
                          const isOverdue = !!m.due_date && m.due_date.substring(0, 10) < new Date().toISOString().substring(0, 10) && !isDone;
                          const linkedTasks = projectTasks.filter((t) => t.milestone_id === m.id);
                          const completedLinked = linkedTasks.filter((t) => ['done', 'completed'].includes((t.status || '').toLowerCase())).length;
                          const progressPct = linkedTasks.length > 0 ? Math.round((completedLinked / linkedTasks.length) * 100) : 0;

                          return (
                            <div
                              key={m.id}
                              className={`p-3.5 rounded-lg border transition-all space-y-3 ${
                                isDone
                                  ? 'bg-emerald-50/30 border-emerald-200'
                                  : isOngoing
                                  ? 'bg-teal-50/20 border-teal-200'
                                  : 'bg-white border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              {/* Header Row */}
                              <div className="flex items-start justify-between gap-2">
                                <div className="space-y-1 flex-1 min-w-0">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                                      Step {m.order ?? m.sort_order ?? idx + 1}
                                    </span>
                                    <h4 className={`text-xs font-bold truncate ${isDone ? 'line-through text-slate-500' : 'text-slate-800'}`}>
                                      {m.title}
                                    </h4>
                                  </div>
                                  {m.description && (
                                    <p className="text-[11px] text-slate-600 line-clamp-2 leading-relaxed">
                                      {m.description}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center space-x-1.5 shrink-0">
                                  {!isViewer && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setNewTaskForm({
                                          title: '',
                                          description: '',
                                          priority: 'medium',
                                          due_date: m.due_date ? m.due_date.substring(0, 10) : '',
                                          milestone_id: m.id,
                                        });
                                        setActiveTab('tasks');
                                        setShowAddTaskModal(true);
                                      }}
                                      className="h-6 text-[11px] px-2 text-brand-teal border-brand-teal/40 hover:bg-brand-teal/10 flex items-center space-x-1 font-semibold"
                                      title={`Add task to ${m.title}`}
                                    >
                                      <Plus className="w-3 h-3 mr-0.5" />
                                      <span>+ Add task</span>
                                    </Button>
                                  )}
                                  <button
                                    type="button"
                                    disabled={!canManageProjects}
                                    onClick={() => handleToggleMilestoneStatus(m)}
                                    title={!canManageProjects ? 'Only Owners and Managers can change milestone status' : 'Click to cycle milestone status'}
                                    className={`shrink-0 transition-transform active:scale-95 ${!canManageProjects ? 'cursor-default opacity-85' : 'cursor-pointer'}`}
                                  >
                                    <Badge
                                      variant={isDone ? 'completed' : isOngoing ? 'in_progress' : 'secondary'}
                                      className="text-[10px] capitalize px-2 py-0.5 flex items-center space-x-1"
                                    >
                                      {isDone ? (
                                        <CheckCircle2 className="h-3 w-3 text-teal-600" />
                                      ) : isOngoing ? (
                                        <Clock className="h-3 w-3 text-brand-teal" />
                                      ) : (
                                        <Circle className="h-3 w-3 text-slate-400" />
                                      )}
                                      <span>{m.status.replace('_', ' ')}</span>
                                    </Badge>
                                  </button>
                                </div>
                              </div>

                              {/* Deliverable Meta (Due date & Linked tasks count) */}
                              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500 pt-2 border-t border-slate-100 dark:border-brand-border/40">
                                <div className="flex items-center space-x-4">
                                  {m.due_date && (
                                    <span className="flex items-center space-x-1">
                                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                      <span>Due {new Date(m.due_date).toLocaleDateString()}</span>
                                    </span>
                                  )}
                                  {isOverdue && (
                                    <span className="flex items-center space-x-0.5 text-red-600 font-semibold text-[10px]">
                                      <AlertCircle className="h-3 w-3" />
                                      <span>Overdue</span>
                                    </span>
                                  )}
                                  <span className="flex items-center space-x-1 font-medium">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-slate-400" />
                                    <span>
                                      {completedLinked} / {linkedTasks.length} linked tasks completed
                                    </span>
                                  </span>
                                </div>
                                {linkedTasks.length > 0 && (
                                  <span className="font-mono text-[11px] font-semibold text-brand-teal">
                                    {progressPct}%
                                  </span>
                                )}
                              </div>

                              {/* Mini Progress Bar */}
                              {linkedTasks.length > 0 && (
                                <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                                  <div
                                    className="bg-brand-teal h-full transition-all duration-300"
                                    style={{ width: `${progressPct}%` }}
                                  />
                                </div>
                              )}

                              {/* Linked Tasks Section */}
                              <div className="pt-2 space-y-2 border-t border-slate-100 dark:border-brand-border/30">
                                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between">
                                  <span>Linked Tasks ({linkedTasks.length})</span>
                                  {!isViewer && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setNewTaskForm({
                                          title: '',
                                          description: '',
                                          priority: 'medium',
                                          due_date: m.due_date ? m.due_date.substring(0, 10) : '',
                                          milestone_id: m.id,
                                        });
                                        setActiveTab('tasks');
                                        setShowAddTaskModal(true);
                                      }}
                                      className="text-brand-teal hover:underline text-[11px] font-semibold flex items-center space-x-0.5"
                                    >
                                      <Plus className="w-3 h-3 inline" />
                                      <span>+ Add task</span>
                                    </button>
                                  )}
                                </div>
                                {linkedTasks.length === 0 ? (
                                  <div className="text-xs text-slate-400 italic py-1.5 px-2.5 rounded bg-slate-50/50 dark:bg-[#162A27]/30 border border-dashed border-slate-200 dark:border-brand-border/40 flex items-center justify-between">
                                    <span>No tasks linked to this milestone yet.</span>
                                    {!isViewer && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setNewTaskForm({
                                            title: '',
                                            description: '',
                                            priority: 'medium',
                                            due_date: m.due_date ? m.due_date.substring(0, 10) : '',
                                            milestone_id: m.id,
                                          });
                                          setActiveTab('tasks');
                                          setShowAddTaskModal(true);
                                        }}
                                        className="text-brand-teal hover:underline font-semibold ml-2 not-italic text-xs"
                                      >
                                        + Add task
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <div className="space-y-1.5">
                                    {linkedTasks.map((t) => (
                                      <div
                                        key={t.id}
                                        className="p-2 rounded-lg bg-slate-50/60 dark:bg-[#162A27]/50 border border-slate-100 dark:border-brand-border/40 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs"
                                      >
                                        <div className="flex items-center space-x-2 min-w-0 flex-1">
                                          <span
                                            className={`w-2 h-2 rounded-full shrink-0 ${
                                              ['done', 'completed'].includes((t.status || '').toLowerCase())
                                                ? 'bg-emerald-500'
                                                : t.status === 'in_progress'
                                                ? 'bg-blue-500'
                                                : t.status === 'blocked'
                                                ? 'bg-rose-500'
                                                : 'bg-slate-300'
                                            }`}
                                          />
                                          <Link
                                            to={`/tasks/${t.id}`}
                                            className="font-medium text-slate-800 dark:text-brand-ink hover:text-brand-teal truncate"
                                            title={t.title}
                                          >
                                            {t.title}
                                          </Link>
                                          <Badge variant={t.status as any} className="text-[10px] px-1.5 py-0 capitalize shrink-0">
                                            {t.status.replace('_', ' ')}
                                          </Badge>
                                          <Badge variant={t.priority as any} className="text-[9px] uppercase px-1 py-0 shrink-0">
                                            {t.priority}
                                          </Badge>
                                          {t.assignee_name && (
                                            <span className="text-[11px] text-slate-400 truncate shrink-0">
                                              • {t.assignee_name}
                                            </span>
                                          )}
                                        </div>

                                        {/* Controls to Move to another milestone or Unlink */}
                                        {!isViewer && canEditTask(t) && (
                                          <div className="flex items-center space-x-1.5 self-end sm:self-auto shrink-0">
                                            <select
                                              value={t.milestone_id || ''}
                                              onChange={(e) => handleMoveTaskMilestone(t.id, e.target.value || null)}
                                              className="h-6 text-[11px] rounded border border-slate-200 dark:border-brand-border bg-white dark:bg-[#12211F] text-slate-700 dark:text-brand-ink px-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal"
                                              title="Move to another milestone of this project"
                                            >
                                              <option value={m.id}>{m.title} (current)</option>
                                              {projectMilestones
                                                .filter((other) => other.id !== m.id)
                                                .map((other) => (
                                                  <option key={other.id} value={other.id}>
                                                    Move to: {other.title}
                                                  </option>
                                                ))}
                                              <option value="">Unlink from milestone</option>
                                            </select>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => handleMoveTaskMilestone(t.id, null)}
                                              className="h-6 px-1.5 text-[10px] text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                                              title="Unlink task from milestone"
                                            >
                                              Unlink
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Footer Actions (Edit / Delete) */}
                              {canManageProjects && (
                                <div className="flex items-center justify-end space-x-1 pt-2 border-t border-slate-100">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleOpenEditMilestone(m)}
                                    className="h-6 px-1.5 text-slate-500 hover:text-slate-800 text-[11px]"
                                    title="Edit milestone"
                                  >
                                    <Edit3 className="h-3 w-3 mr-1" />
                                    <span>Edit</span>
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setDeleteMilestoneTarget(m)}
                                    className="h-6 px-1.5 text-slate-400 hover:text-red-600 text-[11px]"
                                    title="Delete milestone"
                                  >
                                    <Trash2 className="h-3 w-3 mr-1" />
                                    <span>Delete</span>
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* Edit Form */
              <form onSubmit={handleSaveEdit} className="space-y-4 text-xs">
                <div>
                  <label className="font-semibold text-slate-700">Project Name *</label>
                  <Input
                    required
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="mt-1 text-xs"
                  />
                </div>

                <div>
                  <label className="font-semibold text-slate-700">Scope Notes & Description</label>
                  <textarea
                    rows={3}
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    placeholder="Scope notes, staging URLs, client contact details..."
                    className="w-full mt-1 rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-brand-teal"
                  />
                  <TOSComplianceGuard
                    text={editForm.description || ''}
                    platform={editForm.platform || selectedProject?.platform}
                    onCleanText={(cleaned) => setEditForm({ ...editForm, description: cleaned })}
                    className="mt-2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-slate-700">Category</label>
                    <select
                      value={editForm.category}
                      onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                      className="w-full mt-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
                    >
                      <option value="WordPress">WordPress</option>
                      <option value="WooCommerce">WooCommerce</option>
                      <option value="Custom Plugin">Custom Plugin</option>
                      <option value="API Integration">API Integration</option>
                      <option value="SaaS">SaaS</option>
                      <option value="SEO">SEO</option>
                      <option value="Mobile App">Mobile App</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-semibold text-slate-700">Client Name</label>
                    <Input
                      value={editForm.client_name}
                      onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })}
                      placeholder="e.g. Apex Retail"
                      className="mt-1 text-xs"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-slate-700">Platform / Channel</label>
                    <select
                      value={editForm.platform || 'direct'}
                      onChange={(e) => setEditForm({ ...editForm, platform: e.target.value as PlatformType })}
                      className="w-full mt-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
                    >
                      <option value="fiverr">Fiverr</option>
                      <option value="upwork">Upwork</option>
                      <option value="direct">Direct Client</option>
                      <option value="internal">Internal Project</option>
                      <option value="other">Other Channel</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-semibold text-slate-700">Buyer Handle / Username</label>
                    <Input
                      value={editForm.client_username || ''}
                      onChange={(e) => setEditForm({ ...editForm, client_username: e.target.value })}
                      placeholder="e.g. dkodaa or @clienthandle"
                      className="mt-1 text-xs"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-slate-700">Order / Contract ID</label>
                    <Input
                      value={editForm.platform_order_id || ''}
                      onChange={(e) => setEditForm({ ...editForm, platform_order_id: e.target.value })}
                      placeholder="e.g. FO928174829 or UP-884920"
                      className="mt-1 text-xs"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-slate-700">External Order URL</label>
                    <Input
                      value={editForm.platform_url || ''}
                      onChange={(e) => setEditForm({ ...editForm, platform_url: e.target.value })}
                      placeholder="https://www.fiverr.com/orders/..."
                      className="mt-1 text-xs"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-slate-700">Status</label>
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                      className="w-full mt-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
                    >
                      <option value="active">Active</option>
                      <option value="in_progress">In Progress</option>
                      <option value="in_review">In Review</option>
                      <option value="at_risk">At Risk</option>
                      <option value="on_hold">On Hold</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-semibold text-slate-700">Priority</label>
                    <select
                      value={editForm.priority}
                      onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                      className="w-full mt-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="font-semibold text-slate-700">Target Deadline</label>
                  <Input
                    type="date"
                    value={editForm.deadline}
                    onChange={(e) => setEditForm({ ...editForm, deadline: e.target.value })}
                    className="mt-1 text-xs"
                  />
                </div>

                {/* Progress Mode & Manual Override */}
                <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50 space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="font-semibold text-slate-700">Progress Calculation</label>
                    <div className="flex space-x-1">
                      <Button
                        type="button"
                        size="sm"
                        variant={editForm.progress_mode === 'automatic' ? 'secondary' : 'ghost'}
                        onClick={() => setEditForm({ ...editForm, progress_mode: 'automatic' })}
                        className="text-[10px] h-7 px-2"
                      >
                        Auto (Tasks Mean)
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={editForm.progress_mode === 'manual' ? 'secondary' : 'ghost'}
                        onClick={() => setEditForm({ ...editForm, progress_mode: 'manual' })}
                        className="text-[10px] h-7 px-2"
                      >
                        Manual Override
                      </Button>
                    </div>
                  </div>

                  {editForm.progress_mode === 'manual' && (
                    <>
                      <div>
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] text-slate-600">Override Percentage</label>
                          <span className="font-mono font-bold text-slate-800">{editForm.progress_percent}%</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={editForm.progress_percent}
                          onChange={(e) => setEditForm({ ...editForm, progress_percent: Number(e.target.value) })}
                          className="w-full accent-brand-teal mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] text-slate-600">Audit Override Reason *</label>
                        <Input
                          required={editForm.progress_mode === 'manual'}
                          value={editForm.manual_override_reason}
                          onChange={(e) => setEditForm({ ...editForm, manual_override_reason: e.target.value })}
                          placeholder="e.g. Client requested priority push"
                          className="mt-1 text-xs"
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="flex justify-end space-x-2 pt-2">
                  <Button type="button" variant="ghost" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-brand-teal text-white">
                    Save Changes
                  </Button>
                </div>
              </form>
            )}
          </div>

          {/* Drawer Footer with Delete Action (Owner ONLY) */}
          <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
            <span className="text-[11px] text-slate-400 font-mono">ID: {selectedProject.id}</span>
            {isOwner && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteProjectTarget(selectedProject)}
                className="text-red-600 border-red-200 hover:bg-red-50 text-xs flex items-center space-x-1"
              >
                <Trash2 className="h-3.5 w-3.5" />
                <span>Delete Project</span>
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Delete Project Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!deleteProjectTarget}
        itemType="Project"
        itemName={deleteProjectTarget?.name}
        description={deleteProjectTarget ? `Are you sure you want to permanently delete "${deleteProjectTarget.name}"? All associated tasks will also be deleted. This action cannot be undone.` : undefined}
        confirmLabel="Yes, Delete Project"
        onClose={() => setDeleteProjectTarget(null)}
        onConfirm={confirmDeleteProject}
      />

      {/* Delete Milestone Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!deleteMilestoneTarget}
        title="Delete Milestone?"
        itemType="Milestone"
        itemName={deleteMilestoneTarget?.title}
        description={deleteMilestoneTarget ? `Are you sure you want to delete milestone "${deleteMilestoneTarget.title}"? Linked tasks survive and are NEVER deleted — their milestone association will simply be set to unlinked (no milestone).` : undefined}
        confirmLabel="Yes, Delete Milestone"
        onClose={() => setDeleteMilestoneTarget(null)}
        onConfirm={handleConfirmDeleteMilestone}
      />

      {/* Create Project Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4 animate-in fade-in zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold text-brand-navy">Create New Project</h2>
            <form onSubmit={handleCreateProject} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700">Project Name *</label>
                <Input
                  required
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                  placeholder="e.g. WooCommerce Store Redesign"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Client Name</label>
                <Input
                  value={newProject.client_name}
                  onChange={(e) => setNewProject({ ...newProject, client_name: e.target.value })}
                  placeholder="e.g. Apex Retail Group"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Scope Notes & Description</label>
                <textarea
                  rows={3}
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  placeholder="Scope notes, staging URLs, client contact details, credentials..."
                  className="w-full mt-1 rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-brand-teal"
                />
                <TOSComplianceGuard
                  text={newProject.description || ''}
                  platform={newProject.platform}
                  onCleanText={(cleaned) => setNewProject({ ...newProject, description: cleaned })}
                  className="mt-2"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700">Platform / Channel</label>
                  <select
                    value={newProject.platform || 'direct'}
                    onChange={(e) => setNewProject({ ...newProject, platform: e.target.value as PlatformType })}
                    className="w-full mt-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
                  >
                    <option value="fiverr">Fiverr</option>
                    <option value="upwork">Upwork</option>
                    <option value="direct">Direct Client</option>
                    <option value="internal">Internal Project</option>
                    <option value="other">Other Channel</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Buyer Handle / Username</label>
                  <Input
                    value={newProject.client_username}
                    onChange={(e) => setNewProject({ ...newProject, client_username: e.target.value })}
                    placeholder="e.g. dkodaa or @clienthandle"
                    className="mt-1 text-xs"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700">Order / Contract ID</label>
                  <Input
                    value={newProject.platform_order_id}
                    onChange={(e) => setNewProject({ ...newProject, platform_order_id: e.target.value })}
                    placeholder="e.g. FO928174829 or UP-884920"
                    className="mt-1 text-xs"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">External Order URL</label>
                  <Input
                    value={newProject.platform_url}
                    onChange={(e) => setNewProject({ ...newProject, platform_url: e.target.value })}
                    placeholder="https://www.fiverr.com/orders/..."
                    className="mt-1 text-xs"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700">Category</label>
                  <select
                    value={newProject.category}
                    onChange={(e) => setNewProject({ ...newProject, category: e.target.value })}
                    className="w-full mt-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
                  >
                    <option value="WordPress">WordPress</option>
                    <option value="WooCommerce">WooCommerce</option>
                    <option value="Custom Plugin">Custom Plugin</option>
                    <option value="API Integration">API Integration</option>
                    <option value="SaaS">SaaS</option>
                    <option value="SEO">SEO</option>
                    <option value="Mobile App">Mobile App</option>
                    <option value="Maintenance">Maintenance</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Priority</label>
                  <select
                    value={newProject.priority}
                    onChange={(e) => setNewProject({ ...newProject, priority: e.target.value })}
                    className="w-full mt-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700">Status</label>
                  <select
                    value={newProject.status}
                    onChange={(e) => setNewProject({ ...newProject, status: e.target.value })}
                    className="w-full mt-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
                  >
                    <option value="active">Active</option>
                    <option value="in_progress">In Progress</option>
                    <option value="in_review">In Review</option>
                    <option value="at_risk">At Risk</option>
                    <option value="on_hold">On Hold</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Target Deadline (Optional)</label>
                  <Input
                    type="date"
                    value={newProject.deadline}
                    onChange={(e) => setNewProject({ ...newProject, deadline: e.target.value })}
                    className="mt-1 text-xs"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2 pt-3">
                <Button type="button" variant="ghost" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-brand-teal hover:bg-brand-teal-action text-white">
                  Create Project
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Smart 1-Click Multi-Channel Order Importer */}
      <OrderImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onProjectCreated={(created) => {
          fetchProjects();
          showToast(`Order #${created?.platform_order_id || ''} imported and project created successfully!`);
        }}
      />

      {/* Multi-Channel Communication & Handover Modal */}
      {deliveryModalProject && (
        <ChannelDeliveryModal
          isOpen={Boolean(deliveryModalProject)}
          onClose={() => setDeliveryModalProject(null)}
          project={deliveryModalProject}
          milestones={projectMilestones.length > 0 && selectedProject?.id === deliveryModalProject.id ? projectMilestones : (deliveryModalProject as any).milestones || []}
          clientPhone={(deliveryModalProject as any).client_phone}
          clientEmail={(deliveryModalProject as any).client_email}
          defaultChannel={deliveryModalProject.platform as any}
        />
      )}
      </div>
    </PullToRefresh>
  );
}
